import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import * as Sentry from '@sentry/react';
import { apiKeyEnvironment } from '../../../constants/enum';
import {
  apiKeyBoxBg,
  gray40,
  gray100,
  primaryButtonBackgroundColor,
  red20,
  red50,
  tertiaryButtonBackgroundColor,
  yellow50,
  yellow_overlay10,
} from '../../../constants/colors';
import { dialogText, dialogTitle, regularXs } from '../../../constants/location';
import type { ApiKeyCreatedResponse, ApiKeyEnvironmentType } from '../../../types/apiKey';
import { createApiKey } from '../../../api/firebase/function/apiKeyFunction';
import CustomTextfield from '../../uiParts/form/CustomTextfield';
import CustomSelectField from '../../uiParts/form/CustomSelectField';
import CustomDatePicker from '../../uiParts/form/CustomDatePicker';
import dayjs from 'dayjs';

interface Props {
  open: boolean;
  companyId?: string;
  onClose: () => void;
  onCreated: () => void;
}

const styles = {
  dialog: {
    '& .MuiPaper-root': {
      border: '1px solid' + gray40,
      boxSizing: 'border-box',
      margin: '16px',
      padding: '40px',
      width: '480px',
      '@media screen and (max-width:960px)': {
        padding: '32px 16px',
      },
    },
  },
  dialogHeader: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '24px',
  },
  dialogTitle,
  fieldWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  noticeBox: {
    alignItems: 'flex-start',
    backgroundColor: yellow_overlay10,
    border: `1px solid ${yellow50}`,
    borderRadius: '4px',
    display: 'flex',
    gap: '8px',
    marginBottom: '16px',
    padding: '12px',
  },
  noticeIcon: {
    color: yellow50,
    flexShrink: 0,
    fontSize: '20px',
    marginTop: '2px',
  },
  noticeText: {
    ...dialogText,
    color: gray100,
  },
  keyBox: {
    alignItems: 'center',
    backgroundColor: apiKeyBoxBg,
    borderRadius: '4px',
    display: 'flex',
    fontFamily: 'monospace',
    gap: '8px',
    padding: '12px 16px',
    wordBreak: 'break-all' as const,
  },
  keyBoxText: {
    flex: 1,
    fontSize: '14px',
  },
  metaText: {
    ...regularXs,
    marginTop: '16px',
  },
  errorBox: {
    backgroundColor: red20,
    border: `1px solid ${red50}`,
    borderRadius: '4px',
    color: red50,
    fontSize: '14px',
    padding: '8px 12px',
    marginTop: '8px',
  },
  dialogActions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: '32px',
  },
  actionButton: {
    height: '44px',
    minWidth: '120px',
  },
};

const ApiKeyCreateDialog: React.FC<Props> = ({ open, companyId, onClose, onCreated }) => {
  const [displayName, setDisplayName] = useState('');
  const [environment, setEnvironment] = useState<ApiKeyEnvironmentType>(apiKeyEnvironment.live);
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);
  const [ipText, setIpText] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [displayNameError, setDisplayNameError] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [created, setCreated] = useState<ApiKeyCreatedResponse | null>(null);
  const [copied, setCopied] = useState(false);

  const reset = (): void => {
    setDisplayName('');
    setEnvironment(apiKeyEnvironment.live);
    setExpiresAt(null);
    setIpText('');
    setDisplayNameError('');
    setErrorMessage('');
    setCreated(null);
    setCopied(false);
  };

  const handleClose = (): void => {
    if (submitting) return;
    if (created) {
      onCreated();
    }
    reset();
    onClose();
  };

  const handleSubmit = async (): Promise<void> => {
    if (!displayName.trim()) {
      setDisplayNameError('表示名を入力してください。');
      return;
    }
    setSubmitting(true);
    setDisplayNameError('');
    setErrorMessage('');
    try {
      const ipList = ipText
        .split('\n')
        .map(l => l.trim())
        .filter(l => l.length > 0);
      // 日付選択のため、選択日はその日の終わり（23:59:59.999）まで有効として扱う
      const expiresAtIso = expiresAt ? dayjs(expiresAt).endOf('day').toISOString() : null;
      const result = await createApiKey({
        company_id: companyId,
        display_name: displayName.trim(),
        environment,
        expires_at: expiresAtIso,
        ip_allowlist: ipList,
      });
      setCreated(result);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'APIキーの発行に失敗しました。';
      setErrorMessage(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async (): Promise<void> => {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(created.plaintext_key);
      setCopied(true);
      setErrorMessage('');
    } catch (e) {
      setCopied(false);
      Sentry.captureException(e);
      setErrorMessage(
        'クリップボードへのコピーに失敗しました。手動でキーを選択してコピーしてください。',
      );
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} sx={styles.dialog}>
      <Box sx={styles.dialogHeader}>
        <Typography sx={styles.dialogTitle}>
          {created ? 'APIキーを発行しました' : 'APIキーを発行'}
        </Typography>
        <IconButton onClick={handleClose} disabled={submitting} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      {created ? (
        <Box>
          <Box sx={styles.noticeBox}>
            <WarningAmberRoundedIcon sx={styles.noticeIcon} />
            <Typography sx={styles.noticeText}>
              このキーは<strong>このダイアログを閉じると二度と表示されません</strong>。
              忘れずに安全な場所へ保管してください。
            </Typography>
          </Box>
          <Box sx={styles.keyBox}>
            <Box sx={styles.keyBoxText}>{created.plaintext_key}</Box>
            <Tooltip title={copied ? 'コピー済み' : 'コピー'}>
              <IconButton onClick={handleCopy} aria-label="APIキーをコピー" size="small">
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          {errorMessage && <Box sx={styles.errorBox}>{errorMessage}</Box>}
          <Typography sx={styles.metaText}>
            表示名: {created.display_name} / 環境: {created.environment} / 末尾4桁:{' '}
            {created.key_last4}
          </Typography>
          <Box sx={styles.dialogActions}>
            <Button onClick={handleClose} sx={[primaryButtonBackgroundColor, styles.actionButton]}>
              閉じる
            </Button>
          </Box>
        </Box>
      ) : (
        <Box sx={styles.fieldWrapper}>
          <CustomTextfield
            labelText="表示名"
            requiredText="必須"
            value={displayName}
            onChange={(e): void => {
              setDisplayName(e.target.value);
              if (displayNameError) setDisplayNameError('');
            }}
            placeholder="ERP連携 本番"
            inputWidth="100%"
            fontSizeIs14px
            formMb="0"
            error={Boolean(displayNameError)}
            helperText={displayNameError}
          />
          <CustomSelectField
            labelText="環境"
            value={environment}
            onChange={(e): void => setEnvironment(e.target.value as ApiKeyEnvironmentType)}
            list={[
              { value: apiKeyEnvironment.live, option: '本番 (live)' },
              { value: apiKeyEnvironment.test, option: 'テスト (test)' },
            ]}
            formMb="0"
          />
          <CustomDatePicker
            labelText="有効期限 (任意)"
            isRequired={false}
            value={expiresAt}
            onChange={(value): void => setExpiresAt(value)}
            width="100%"
            formMb="zero"
            fontSizeIs14px
            height="51px"
            paddingIs10px8px
            hideHelperText
          />
          <CustomTextfield
            labelText="IPホワイトリスト (任意、CIDR を 1 行 1 件)"
            multiline
            minRows={3}
            maxRows={8}
            value={ipText}
            onChange={(e): void => setIpText(e.target.value)}
            placeholder="203.0.113.0/24"
            inputWidth="100%"
            fontSizeIs14px
            formMb="0"
          />
          {errorMessage && <Box sx={styles.errorBox}>{errorMessage}</Box>}
          <Box sx={styles.dialogActions}>
            <Button
              onClick={handleClose}
              disabled={submitting}
              sx={[tertiaryButtonBackgroundColor, styles.actionButton]}
            >
              キャンセル
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
              sx={[primaryButtonBackgroundColor, styles.actionButton]}
            >
              発行する
            </Button>
          </Box>
        </Box>
      )}
    </Dialog>
  );
};

export default ApiKeyCreateDialog;
